/* -------------------------------------------------------------------------- */
/*                                  Functions                                 */
/* -------------------------------------------------------------------------- */

import { HttpErrorResponse } from '@angular/common/http';
import * as moment from 'moment';
import { Observable, of, throwError, timer } from 'rxjs';
import { mergeMap, retryWhen, switchMap } from 'rxjs/operators';
import { BlobStorageErrorCode } from './models';

export function copyCellToClipboard(val: string) {
    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = val;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);
}

export function setColWidth(widthPercent: number | null) {
    return { flex: widthPercent ? `0 0 ${widthPercent}%` : '1' };
}

export function generateUUID() {
    // Public Domain/MIT
    var d = new Date().getTime(); //Timestamp
    var d2 = (typeof performance !== 'undefined' && performance.now && performance.now() * 1000) || 0; //Time in microseconds since page-load or 0 if unsupported
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = Math.random() * 16; //random number between 0 and 16
        if (d > 0) {
            //Use timestamp until depleted
            r = ((d + r) % 16) | 0;
            d = Math.floor(d / 16);
        } else {
            //Use microseconds since page-load if supported
            r = ((d2 + r) % 16) | 0;
            d2 = Math.floor(d2 / 16);
        }
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
}

export function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) {
        return '0 Bytes';
    }
    const k = 1024;
    const dm = decimals <= 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export function downloadFile(url: string, fileName: string) {
    const link = document.createElement('a');
    link.target = '_blank';
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
}

export function arrayFromEnum(enumObject) {
    return Object.keys(enumObject)
        .filter(value => isNaN(Number(value)) === true)
        .map(key => {
            return {
                label: key,
                value: enumObject[key],
            };
        });
}

export function getEnumKeyByEnumValue(myEnum, enumValue) {
    let keys = Object.keys(myEnum).filter(x => myEnum[x] == enumValue);
    return keys.length > 0 ? keys[0] : null;
}

/**
 * Removes properties with null values from an object recursively.
 * @param obj - The object from which to remove properties with null values.
 * @returns A new object with properties containing null values removed.
 */
export const removePropertiesWithNullValues = obj =>
    Object.entries(obj)
        .map(([k, v]) => [k, v && v instanceof Object && !(v instanceof Date) ? removePropertiesWithNullValues(v) : v])
        .reduce((a, [k, v]) => (v == null ? a : ((a[k] = v), a)), {});

/**
 * Replaces the date value in a JSON object with a local date string in the format 'DD/MM/YYYY'.
 * @param key - The key of the current property being processed.
 * @param value - The value of the current property being processed.
 * @returns The modified value for the property.
 */
export const jsonLocalDateReplacer = function (key, value) {
    if (this[key] instanceof Date) {
        return moment.parseZone(this[key] as Date).format('DD/MM/YYYY');
    }

    return value;
};

export type ListPeriodList = 'Oggi' | 'Ieri' | 'Questa settimana' | 'Settimana scorsa' | 'Questo mese' | 'Mese scorso' | `Quest'anno` | 'Anno scorso';

export const createPeriodoListFromLabelsPeriodo = (
    labelsPeriodoList: Array<ListPeriodList> = [
        'Oggi',
        'Ieri',
        'Questa settimana',
        'Settimana scorsa',
        'Questo mese',
        'Mese scorso',
        `Quest'anno`,
        'Anno scorso',
    ],
) => {
    const defaultPeriodFilters = labelsPeriodoList.map((label, i) => {
        const today = new Date();
        const dayInMilli = 86400000;
        const weekInMilli = 604800000;
        const dallaDataOggi = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
        const allaDataOggi = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
        const dallaDataIeri = new Date(dallaDataOggi.getTime() - dayInMilli);
        const allaDataIeri = new Date(allaDataOggi.getTime() - dayInMilli);
        const giornoSettimana = today.getDay();
        const dallaDataSettimanaCorrente = new Date(dallaDataOggi.getTime() - (giornoSettimana - 1) * dayInMilli);
        const allaDataSettimanaCorrente = new Date(allaDataOggi.getTime() + (7 - giornoSettimana) * dayInMilli);
        const dallaDataSettimanaScorsa = new Date(dallaDataSettimanaCorrente.getTime() - weekInMilli);
        const allaDataSettimanaScorsa = new Date(allaDataSettimanaCorrente.getTime() - weekInMilli);
        const primogiornoMeseProx = new Date(today.getFullYear(), today.getMonth() + 1, 1, 23, 59, 59);
        const dallaDataMeseCorrente = new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0);
        const allaDataMeseCorrente = new Date(primogiornoMeseProx.getTime() - dayInMilli);
        const allaDataMeseScorso = new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59);
        const dallaDataMeseScorso = new Date(allaDataMeseScorso.getFullYear(), allaDataMeseScorso.getMonth(), 1, 0, 0, 0);
        const dallaDataAnno = new Date(today.getFullYear(), 0, 1, 0, 0, 0);
        const allaDataAnno = new Date(today.getFullYear(), 11, 31, 23, 59, 59);
        const dallaDataAnnoScorso = new Date(today.getFullYear() - 1, 0, 1, 0, 0, 0);
        const allaDataAnnoScorso = new Date(today.getFullYear() - 1, 11, 31, 23, 59, 59);
        switch (i) {
            case 0:
                return { descrizione: label, dallaData: dallaDataOggi, allaData: allaDataOggi };
            case 1:
                return { descrizione: label, dallaData: dallaDataIeri, allaData: allaDataIeri };
            case 2:
                return { descrizione: label, dallaData: dallaDataSettimanaCorrente, allaData: allaDataSettimanaCorrente };
            case 3:
                return { descrizione: label, dallaData: dallaDataSettimanaScorsa, allaData: allaDataSettimanaScorsa };
            case 4:
                return { descrizione: label, dallaData: dallaDataMeseCorrente, allaData: allaDataMeseCorrente };
            case 5:
                return { descrizione: label, dallaData: dallaDataMeseScorso, allaData: allaDataMeseScorso };
            case 6:
                return { descrizione: label, dallaData: dallaDataAnno, allaData: allaDataAnno };
            case 7:
                return { descrizione: label, dallaData: dallaDataAnnoScorso, allaData: allaDataAnnoScorso };
        }
    });

    return defaultPeriodFilters.filter(periodFilter => labelsPeriodoList.includes(periodFilter.descrizione));
};
/* -------------------------------------------------------------------------- */
/*                              DeveExtreme Utils                             */
/* -------------------------------------------------------------------------- */

/**
 * Formatta il summary total di DevExtreme con un testo custom e con la sintassi Europea degli euro approssimati a due decimali dopo la virgola
 *
 * @param cellInfo
 * @param customText
 */
export function totaleFormat(cellInfo: any, customText = 'Totale', separator = ': ') {
    return (
        `${customText}${separator}` +
        cellInfo.value.toLocaleString('it-IT', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
        })
    );
}

/**
 * Handles retry logic for observables when a specific Blob Storage authentication error occurs.
 *
 * This operator intercepts errors emitted by the source observable and checks if the error status
 * matches `BlobStorageErrorCode.AutenticazioneScaduta`. If so, it optionally invokes a provided
 * `action` (which should return an observable) after a 150ms delay before retrying the source observable.
 * If no action is provided, it retries immediately. Other errors are re-thrown.
 *
 * @template T The type of items emitted by the source observable.
 * @param action Optional function that returns an observable to be executed when the authentication error occurs.
 * @returns An operator function that can be used with RxJS `pipe` to handle authentication expiry errors.
 */
export function onContainerSasUriExpiry<T>(action?: () => Observable<any>): (source: Observable<T>) => Observable<T> {
    return (source: Observable<T>): Observable<T> =>
        source.pipe(
            retryWhen(errors =>
                errors.pipe(
                    mergeMap((error: HttpErrorResponse) => {
                        if (error.status === BlobStorageErrorCode.AutenticazioneScaduta) {
                            return action
                                ? timer(150).pipe(switchMap(() => action())) // Wait for 150ms before invoking the action
                                : of(null); // If no action is provided, just retry immediately
                        }
                        return throwError(() => error);
                    }),
                ),
            ),
        );
}

/**
 * Formatta il summary total di DevExtreme con la sintassi Europea degli euro approssimati a due decimali dopo la virgola
 *
 * @param cellInfo
 */
export function currencyFormat(cellInfo: any) {
    return cellInfo.value.toLocaleString('it-IT', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
    });
}

/**
 * Formatta il periodo Intrastat (es. 20231) a video nel formato "Anno: 2023, 1° trimestre"
 * @param periodo 
 */
export function getDescrizionePeriodo(periodo: number): string {
    if (!periodo) {
        return '';
    }
    const anno = Math.floor(periodo / 10);
    const trimestre = periodo % 10;
    return `Anno: ${anno}, ${trimestre}° trimestre`;
}

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

export const nomiMesi = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

export const MimeTypes = {
    '3dmf': 'x-world/x-3dmf',
    '3dm': 'x-world/x-3dmf',
    avi: 'video/x-msvideo',
    ai: 'application/postscript',
    bin: 'application/octet-stream',
    bmp: 'image/bmp',
    cab: 'application/x-shockwave-flash',
    c: 'text/plain',
    'c++': 'text/plain',
    class: 'application/java',
    css: 'text/css',
    csv: 'text/comma-separated-values',
    cdr: 'application/cdr',
    doc: 'application/msword',
    dot: 'application/msword',
    docx: 'application/msword',
    dwg: 'application/acad',
    eps: 'application/postscript',
    exe: 'application/octet-stream',
    gif: 'image/gif',
    gz: 'application/gzip',
    gtar: 'application/x-gtar',
    flv: 'video/x-flv',
    fh4: 'image/x-freehand',
    fh5: 'image/x-freehand',
    fhc: 'image/x-freehand',
    help: 'application/x-helpfile',
    hlp: 'application/x-helpfile',
    html: 'text/html',
    htm: 'text/html',
    ico: 'image/x-icon',
    imap: 'application/x-httpd-imap',
    inf: 'application/inf',
    jpe: 'image/jpeg',
    jpeg: 'image/jpeg',
    jpg: 'image/jpeg',
    js: 'application/x-javascript',
    json: 'application/json',
    java: 'text/x-java-source',
    latex: 'application/x-latex',
    log: 'text/plain',
    m3u: 'audio/x-mpequrl',
    midi: 'audio/midi',
    mid: 'audio/midi',
    mov: 'video/quicktime',
    mp3: 'audio/mpeg',
    mpeg: 'video/mpeg',
    mpg: 'video/mpeg',
    mp2: 'video/mpeg',
    ogg: 'application/ogg',
    p7m: 'application/pkcs7-mime',
    phtml: 'application/x-httpd-php',
    php: 'application/x-httpd-php',
    pdf: 'application/pdf',
    pgp: 'application/pgp',
    png: 'image/png',
    pps: 'application/mspowerpoint',
    ppt: 'application/mspowerpoint',
    ppz: 'application/mspowerpoint',
    pot: 'application/mspowerpoint',
    ps: 'application/postscript',
    qt: 'video/quicktime',
    qd3d: 'x-world/x-3dmf',
    qd3: 'x-world/x-3dmf',
    qxd: 'application/x-quark-express',
    rar: 'application/x-rar-compressed',
    ra: 'audio/x-realaudio',
    ram: 'audio/x-pn-realaudio',
    rm: 'audio/x-pn-realaudio',
    rtf: 'text/rtf',
    spr: 'application/x-sprite',
    sprite: 'application/x-sprite',
    stream: 'audio/x-qt-stream',
    swf: 'application/x-shockwave-flash',
    svg: 'text/xml-svg',
    sgml: 'text/x-sgml',
    sgm: 'text/x-sgml',
    tar: 'application/x-tar',
    tiff: 'image/tiff',
    tif: 'image/tiff',
    tgz: 'application/x-compressed',
    tex: 'application/x-tex',
    txt: 'text/plain',
    vob: 'video/x-mpg',
    wav: 'audio/x-wav',
    wrl: 'model/vrml',
    xla: 'application/msexcel',
    xls: 'application/msexcel',
    xlc: 'application/vnd.ms-excel',
    xml: 'text/xml',
    zip: 'application/zip',
};
